import React, { useRef, useState } from "react";
import { Link, Switch, Route } from "react-router-dom";
import classNames from "classnames";
import Avatar from "../avatar/avatar.js";
import AgentBar from "./agent_bar/agent_bar.js";
import ProfileSettings from "./profile_settings/profile_settings.js";
import Directory from "./directory/directory.js";
import BroadcastListDetailPanel from "./directory/detail_broadcast_list.js";
import BroadcastListPanel from "./directory/new_broadcast_list.js";
import BroadcastListNamePanel from "./directory/new_broadcast_list_name.js";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import Agente from "../models/Agente";
import { renderToString } from "react-dom/server";

const sanitizeEmail = (email) =>
  (email || "")
    .toString()
    .trim()
    // .toLowerCase();

const normalizeEmailWithSpaces = (email) =>
  sanitizeEmail(email).replace(/\./g, " ").replace(/\s+/g, " ").trim();

const normalizeEmailComparable = (email) =>
  normalizeEmailWithSpaces(email).replace(/\s+/g, "");

const toDotEmail = (email) => normalizeEmailWithSpaces(email).replace(/ /g, ".");

const getEmailVariants = (email) => {
  const withSpaces = normalizeEmailWithSpaces(email);
  const withDots = toDotEmail(email);
  return Array.from(new Set([withSpaces, withDots].filter(Boolean)));
};
const getCurrentAgentEmailVariants = (currentAgent) => {
  const rawEmails = [
    currentAgent?.email,
    currentAgent?.usuario?.emailreal,
    currentAgent?.usuario?.email,
  ].filter(Boolean);

  return Array.from(new Set(rawEmails.flatMap((email) => getEmailVariants(email))));
};

// const normalizeEmailComparable = (email) =>
//   (email || "")
//     .toString()
//     .trim()
//     .toLowerCase()
//     .replace(/\./g, " ")
//     .replace(/\s+/g, "");


export default function ChatListArea(props) {
  const profileSettings = useRef(null);
  const [directoryIsOpen, setDirectoryIsOpen] = useState(false);
  const [broadcastListIsOpen, setBroadcastListIsOpen] = useState(false);
  const [broadcastListNameIsOpen, setBroadcastListNameIsOpen] = useState(false);
  const [broadcastListMembers, setBroadcastListMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");

  const filterOptions = {
    active: "Activos",
    lessThan1Hour: "Menor 1 hr.",
    exhausted: "Agotados",
    unanswered: "Sin contestar",
    unattended: "Sin atender",
    automatic: "Automática",
  };

  const filterColors = {
    active: "#25D366",
    lessThan1Hour: "#128C7E",
    lessThan15Minutes: "#34B7F1",
    exhausted: "#FF4444",
    moreThan3Hours: "#FFA500",
    unanswered: "#9370DB",
    unattended: "#FF69B4",
    automatic: "#ff9900",
  };

  const contacts = props.contactList
    .filter((xs) => {
      const noAgent = xs.br_agent === "";
      const currentAgent = props.currentAgent !== null;
      if (!currentAgent || !props.currentAgent.usuario) return false;
      if (noAgent && props.currentAgent.usuario.grupo.includes(Agente.GROUP_VENTAS_DIGITALES)) return true;
      if (noAgent && props.currentAgent.usuario.grupo.includes(Agente.GROUP_INMUEBLES)) return true;
      if (noAgent) return false;
      const currentGroup = props.currentAgent.usuario.grupo.includes(xs.br_agent);
      if (currentGroup) return true;
      const currentAgentNormalized = getCurrentAgentEmailVariants(props.currentAgent)
      const match = currentAgent ? currentAgentNormalized.includes(xs.br_agent) : false;
      return noAgent || match || currentGroup;
    })
    .sort((x, y) => {
      const yInbound = y.lastMessage.Inbound.Timestamp || 0;
      const yOutbound = y.lastMessage.Outbound.Timestamp || 0;
      const yTimestamp = Math.max(yInbound, yOutbound) || Date.now() / 1000;

      const xInbound = x.lastMessage.Inbound.Timestamp || 0;
      const xOutbound = x.lastMessage.Outbound.Timestamp || 0;
      const xTimestamp = Math.max(xInbound, xOutbound) || Date.now() / 1000;

      return sortOrder === "desc" ? yTimestamp - xTimestamp : xTimestamp - yTimestamp;
    });

  const customFilterAndSearch = (selectedValues, contact) => {
    const inboundTimestamp = contact.lastMessage.Inbound.Timestamp || 0;
    const outboundTimestamp = contact.lastMessage.Outbound.Timestamp || 0;
    const maxTimestamp = Math.max(inboundTimestamp, outboundTimestamp);

    // Si no hay mensajes, usar fecha actual para el cálculo
    const timestamp = maxTimestamp > 0 ? maxTimestamp : Date.now() / 1000;

    const time = window.countdown(new Date(), timestamp * 1000, window.countdown.HOURS + window.countdown.MINUTES);
    const seconds = Math.abs(time.value) / 1000.0;
    const minutes = seconds / 60.0;
    const hours = minutes / 60.0;

    let matches = true;

    if (selectedValues.length === 0) return true;

    if (selectedValues.includes("lessThan1Hour") && hours >= 1) matches = false;
    if (selectedValues.includes("lessThan15Minutes") && minutes >= 15) matches = false;
    if (selectedValues.includes("active") && (hours >= 24 || inboundTimestamp === 0)) matches = false;
    if (selectedValues.includes("exhausted") && hours < 24) matches = false;
    if (selectedValues.includes("moreThan3Hours") && hours <= 3) matches = false;
    if (selectedValues.includes("unanswered") && inboundTimestamp !== 0) matches = false;
    if (selectedValues.includes("unattended") && (outboundTimestamp !== 0 || hours >= 24)) matches = false;
    if (selectedValues.includes("automatic")) {
      const isAutomatic =
        inboundTimestamp !== 0 &&
        outboundTimestamp !== 0 &&
        inboundTimestamp > outboundTimestamp &&
        inboundTimestamp - outboundTimestamp <= 10;
      if (!isAutomatic) matches = false;
    }

    return matches;
  };

  // Updated function to determine the color for a contact based on its properties
  const getContactColor = (contact) => {
    const inboundTimestamp = contact.lastMessage.Inbound.Timestamp || 0;
    const outboundTimestamp = contact.lastMessage.Outbound.Timestamp || 0;
    const maxTimestamp = Math.max(inboundTimestamp, outboundTimestamp);

    // Si no hay mensajes, usar fecha actual para el cálculo
    const timestamp = maxTimestamp > 0 ? maxTimestamp : Date.now() / 1000;

    const time = window.countdown(new Date(), timestamp * 1000, window.countdown.HOURS + window.countdown.MINUTES);
    const seconds = Math.abs(time.value) / 1000.0;
    const minutes = seconds / 60.0;
    const hours = minutes / 60.0;

    // Determine the color based on contact's status
    // if (minutes < 15) return filterColors["lessThan15Minutes"];
    if (hours < 24 && inboundTimestamp !== 0) return filterColors["active"];
    if (hours >= 24) return filterColors["exhausted"];
    if (hours < 1) return filterColors["lessThan1Hour"];
    // if (hours > 3) return filterColors["moreThan3Hours"];
    if (inboundTimestamp === 0) return filterColors["unanswered"];
    if (outboundTimestamp === 0 && hours < 24) return filterColors["unattended"];

    const isAutomatic =
      inboundTimestamp !== 0 &&
      outboundTimestamp !== 0 &&
      inboundTimestamp > outboundTimestamp &&
      inboundTimestamp - outboundTimestamp <= 10;
    if (isAutomatic) return filterColors["automatic"];

    // Default color if no conditions match
    return "#000000"; // Black
  };

  const renderBroadcastListList = (contacts) => {
    if (props.currentAgent === null) {
      return <div></div>;
    } else {
      return props.broadcastListList.map((bcl) => {
        const klass = classNames("chat-list-item d-flex flex-row w-100 p-2 border-bottom", {
          unread: false,
          active: false,
        });

        return (
          <Link to={`/broadcast_lists/${bcl.id}`} key={bcl.id} className={klass}>
            <div className="w-50">
              <div className="name">{bcl.broadcastList}</div>
              <div className="small last-message">{bcl.lastMessageText}</div>
            </div>
            <div className="flex-grow-1 text-right">
              <div className="small time">{formatDateString(new Date(bcl.updatedAt))}</div>
            </div>
          </Link>
        );
      });
    }
  };

  function messageStatusCheck(contact) {
    if (contact.lastMessage.Inbound.Timestamp > contact.lastMessage.Outbound.Timestamp) {
      return null;
    }

    const msg = props.messageList.find((msg) => msg.key === contact.lastMessage.Outbound.ID);

    if (!msg) {
      return null;
    }

    if (msg.status === 0) {
      return <i className="fas fa-check"></i>;
    } else if (msg.status === 1) {
      return <i className="fas fa-check-double"></i>;
    } else if (msg.status === 2) {
      return <i style={{ color: "rgb(90,150,212)" }} className="fas fa-check-double"></i>;
    }
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleFilterClick = (filter) => {
    setActiveFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };

  const handleSortClick = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const Row = ({ data, index, style }) => {
    const contact = data.contacts[index];
    const agentMatch = getCurrentAgentEmailVariants(props.currentAgent).includes(contact.br_agent);
    const groupMatch = props.currentAgent.usuario.grupo.includes(contact.br_agent);
    const defaultMatch = contact.br_agent === "";
    const predicate = contact.id && ( agentMatch || groupMatch || defaultMatch);
    if (!predicate) {
      return null;
    }

    // Para conversaciones nuevas sin mensajes, usar fecha actual en lugar de epoch
    const inboundTimestamp = contact.lastMessage.Inbound.Timestamp || 0;
    const outboundTimestamp = contact.lastMessage.Outbound.Timestamp || 0;
    const maxTimestamp = Math.max(inboundTimestamp, outboundTimestamp);

    // Si no hay mensajes (timestamp = 0), usar fecha actual
    const timestamp = maxTimestamp > 0 ? maxTimestamp * 1000 : Date.now();

    const textPreview =
      inboundTimestamp > outboundTimestamp
        ? contact.lastMessage.Inbound.Text
        : contact.lastMessage.Outbound.Text;

    const expirationTime = window.countdown(
      timestamp + 86400000,
      null,
      window.countdown.HOURS + window.countdown.MINUTES
    );

    let conversationIsExpired = false;
    if (contact && contact.hasOwnProperty("lastMessage")) {
      const last_masg_timestamp = Math.max(contact.lastMessage.Inbound.Timestamp, 1) * 1000;
      conversationIsExpired = new Date().getTime() - last_masg_timestamp > 86400000;
    }

    const expirationTimeLabel =
      expirationTime.value < 0 ? (
        expirationTime.toString()
      ) : (
        <span role="img" aria-label="prohibited">
          🚫
        </span>
      );

    const klass = classNames("chat-list-item d-flex flex-row w-100 p-2 border-bottom" + (contact.hasError ? "errorContact" : ""), {
      unread: contact.Unread,
      active: props.currentConversation === contact.id,
    });

    const handleClick = () => {
      props.setCurrentConversation(contact.idhash);
      if (window.innerWidth < 768) {
        const chatListArea = document.getElementById("chat-list-area");
        if (chatListArea) {
          chatListArea.classList.add("hidden");
        }
      }
    };

    // Get the color for the contact based on its properties
    const avatarColor = getContactColor(contact);

    return (
      <Link
        onClick={handleClick}
        to={`/conversation/${contact.idhash}`}
        key={`${contact.idhash}-${contact.Unread}-${contact.lastMessage.Inbound.ID}`}
        className={klass}
        style={style}
        onMouseEnter={(e) => {
          if (!contact.hasError) return;

          let tooltip = document.querySelector(".tooltip");

          const msg = (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
              </svg>
              &nbsp;
              {contact.hasError?.messageError || "Error desconocido"}
            </>
          );
          tooltip.innerHTML = renderToString(msg);
          tooltip.style.opacity = "1";

          e.target.addEventListener("mousemove", (e) => {
            tooltip.style.top = e.clientY + 15 + "px";
            tooltip.style.left = e.clientX + 15 + "px";
          });

          e.target.addEventListener("pointerleave", (e) => {
            const goingTo = e.relatedTarget;
            if (goingTo && goingTo.classList.contains("error-icon")) {
              // 👌 el mouse va a otro icono, no ocultes el tooltip
              return;
            }
            tooltip.style.opacity = "0";
          });
        }}
      >
        {contact.hasError &&
          <div className="mr-2 d-flex align-items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="red" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
            </svg>
          </div>
        }

        <Avatar value={contact.id} color={avatarColor} />
        <div className="w-50">
          <div className="name">{contact.name}</div>
          <div className="campaign-tag">
            <span className="badge badge-dark">{contact.ExtraFields?.NumeroCliente}</span>
            <span className="badge badge-dark">{contact.ExtraFields?.Producto}</span>
          </div>
          {contact.Campaign && (
            <div className="small campaign-tag">
              <span className="badge badge-info">{contact.Campaign}</span>
            </div>
          )}
          <div className="small last-message">
            <span>{messageStatusCheck(contact)}</span> {textPreview}
          </div>
        </div>
        <div className="flex-grow-1 text-right">
          <div className="small time">{formatDateString(new Date(timestamp))}</div>
          {contact.Unread ? (
            <div className="badge badge-success badge-pill small" id="unread-count">
              1
            </div>
          ) : (
            ""
          )}
          <div>
            {conversationIsExpired ? (
              <div className="badge badge-pill " style={{
                position: "absolute",
                bottom: '10px',
                right: '10px',
              }} >
                <i className="fas fa-clock mr-1"></i>
                Esperando respuesta
              </div>
            ) : (
              <div className="badge badge-pill " style={{
                position: "absolute",
                bottom: '10px',
                right: '10px'
              }}>
                <i className="fas fa-hourglass-half mr-1"></i>
                {expirationTimeLabel}
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const renderContactList = (contacts) => {
    var temp = [];
    const filteredContacts = contacts.filter((item) => {
      if (!temp.includes(item.id)) {
        temp.push(item.id);
        if (searchQuery) {
          const nameMatch = item.name && item.name.toLowerCase().includes(searchQuery);
          const numberMatch = item.number && item.number.toLowerCase().includes(searchQuery);
          if (!(nameMatch || numberMatch)) return false;
        }
        return customFilterAndSearch(activeFilters, item);
      }
      return false;
    });

    if (props.currentAgent === null) {
      return <div></div>;
    } else {
      // Create itemData with timestamp to force re-render when unread status changes
      const itemDataWithTimestamp = {
        contacts: filteredContacts,
        timestamp: Date.now(),
        unreadCount: filteredContacts.filter(c => c.Unread).length
      };

      return (
        <AutoSizer>
          {({ height, width }) => (
            <List
              className="List"
              height={height}
              itemSize={90}
              itemCount={itemDataWithTimestamp.contacts.length}
              itemData={itemDataWithTimestamp}
              width={width}
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      );
    }
  };

  const formatDateString = (date) => {
    const dualize = (x) => (x < 10 ? "0" + x : x);
    const getTime = () => dualize(date.getHours()) + ":" + dualize(date.getMinutes());
    const getDate = () =>
      dualize(date.getDate()) + "/" + dualize(date.getMonth() + 1) + "/" + dualize(date.getFullYear());

    const dateDiff = Math.round((Date.now() - date) / (1000 * 60 * 60 * 24));
    if (dateDiff === 0) {
      return getTime();
    } else if (dateDiff === 1) {
      return "Ayer";
    } else {
      return getDate();
    }
  };

  const loaderClass = classNames("col-12 col-sm-5 col-md-4", {
    "d-flex": !props.appIsLoading,
    "flex-column": !props.appIsLoading,
    "d-none": props.appIsLoading,
  });


  document.addEventListener("DOMContentLoaded", () => {
    let tooltip = document.createElement("div");
    tooltip.className = "tooltip mr-2 d-flex align-items-center";
    document.body.appendChild(tooltip);
  });


  return (
    <div className={loaderClass} id="chat-list-area" style={{ position: "relative", maxHeight: "100%" }}>
      <AgentBar
        currentAgent={props.currentAgent}
        agentImage={props.agentImage}
        tenant={props.tenant}
        templateList={props.templateList}
        logOut={props.logOut}
        logIn={props.logIn}
        profileSettings={profileSettings}
        openDirectory={() => setDirectoryIsOpen(true)}
        quickReplies={props.quickReplies}
        attentionDays={props.attentionDays}
        parameter={props.parameter}
      />
      {props.currentAgent && (
        <>
          <div className="row p-2 border-bottom" style={{ backgroundColor: "#fff" }}>
            <div className="col-12">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar contacto por nombre o teléfono..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{ borderRadius: "20px" }}
                />
                <div className="input-group-append">
                  <span className="input-group-text bg-transparent border-0">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="filter-chips-container p-2 border-bottom">
            <div className="filter-chips d-flex flex-wrap">
              {Object.entries(filterOptions).map(([key, label]) => (
                <div
                  key={key}
                  className={`filter-chip ${activeFilters.includes(key) ? "active" : ""}`}
                  onClick={() => handleFilterClick(key)}
                  style={{
                    backgroundColor: activeFilters.includes(key) ? filterColors[key] : "#e9edef",
                    color: activeFilters.includes(key) ? "#fff" : "#000",
                    margin: "1px",
                    padding: "2px 7px",
                    fontSize: "12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                  }}
                >
                  {label}
                </div>
              ))}
              <div
                className="filter-chip"
                onClick={handleSortClick}
                style={{
                  backgroundColor: "#e9edef",
                  color: "#000",
                  margin: "1px",
                  padding: "2px 7px",
                  fontSize: "12px",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className={`fas fa-sort-${sortOrder === "desc" ? "down" : "up"} mr-1`}
                  style={{ fontSize: "10px" }}
                ></i>
                Fecha
              </div>
            </div>
          </div>
        </>
      )}
      <Switch>
        <Route path="/broadcast_lists/:id">
          <div className="row" style={{ overflow: "auto" }}>
            <BroadcastListDetailPanel
              isOpen={true}
              close={() => window.history.back()}
              directory={props.customerDirectory}
              loadBroadcastLists={props.loadBroadcastLists}
              loadBroadcastListMembers={props.loadBroadcastListMembers}
              appendBroadcastListMembers={props.appendBroadcastListMembers}
              removeBroadcastListMember={props.removeBroadcastListMember}
              removeBroadcastList={props.removeBroadcastList}
              currentAgent={props.currentAgent}
              templateList={props.templateList}
            />
          </div>
        </Route>
        <Route path="/broadcast_lists">
          <div className="row" style={{ overflow: "auto" }}>
            {renderBroadcastListList()}
          </div>
        </Route>
        <Route path="/">
          <div className="row" id="chat-list" style={{ height: "calc(100% - 50px)" }}>
            {renderContactList(contacts)}
          </div>
        </Route>
      </Switch>
      <Directory
        currentAgent={props.currentAgent}
        isOpen={directoryIsOpen}
        close={() => setDirectoryIsOpen(false)}
        openNewBroadcastList={() => setBroadcastListIsOpen(true)}
        templateList={props.templateList}
        directory={props.customerDirectory}
        quickReplies={props.quickReplies}
      />
      <BroadcastListPanel
        isOpen={broadcastListIsOpen}
        close={() => setBroadcastListIsOpen(false)}
        openBroadcastListName={() => setBroadcastListNameIsOpen(true)}
        directory={props.customerDirectory}
        setBroadcastListMembers={setBroadcastListMembers}
        quickReplies={props.quickReplies}
      />
      <BroadcastListNamePanel
        isOpen={broadcastListNameIsOpen}
        close={() => setBroadcastListNameIsOpen(false)}
        cleanUp={() => {
          setBroadcastListIsOpen(false);
          setDirectoryIsOpen(false);
        }}
        loadBroadcastLists={props.loadBroadcastLists}
        broadcastListMembers={broadcastListMembers}
        createBroadcastList={props.createBroadcastList}
        appendBroadcastListMembers={props.appendBroadcastListMembers}
        currentAgent={props.currentAgent}
        quickReplies={props.quickReplies}
      />
      <ProfileSettings profileSettings={profileSettings} currentAgent={props.currentAgent} />
    </div>
  );
}